import { useState, useEffect } from 'react'
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Select,
  Banner,
  Divider,
  SkeletonBodyText,
} from '@shopify/polaris'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { useShopifyAuth } from '../hooks/useShopifyAuth'

// Flow Node Component
function FlowNode({
  iconSrc,
  title,
  subtitle,
  children,
  showConnector = true,
}: {
  iconSrc: string
  title: string
  subtitle: string
  children?: React.ReactNode
  showConnector?: boolean
}) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div style={styles.nodeContainer}>
      <div
        style={{
          ...styles.node,
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          boxShadow: isHovered
            ? '0 8px 16px rgba(255, 107, 53, 0.2)'
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.nodeIcon}>
          <img src={iconSrc} alt={title} style={{ width: '24px', height: '24px' }} />
        </div>
        <div style={styles.nodeContent}>
          <Text as="p" variant="headingSm" fontWeight="bold">
            {title}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {subtitle}
          </Text>
          {children && (
            <div style={styles.nodeChildren}>
              {children}
            </div>
          )}
        </div>
      </div>
      {showConnector && <div style={styles.connector} />}
    </div>
  )
}

// Delay options in minutes
const delayOptions = [
  { label: '30 minutes', value: '30' },
  { label: '1 hour', value: '60' },
  { label: '6 hours', value: '360' },
  { label: '24 hours', value: '1440' },
  { label: '48 hours', value: '2880' },
  { label: '7 days', value: '10080' },
]

function formatDelay(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`
  if (minutes < 1440) return `${minutes / 60} hour${minutes > 60 ? 's' : ''}`
  return `${minutes / 1440} day${minutes > 1440 ? 's' : ''}`
}

export default function Flow() {
  // Initialize Shopify auth
  useShopifyAuth()

  const queryClient = useQueryClient()

  // Check if we're in preview mode
  const isPreviewMode = !window.shopify

  // Mock data for preview
  const mockFlowConfig = {
    enabled: true,
    delay_minutes: 1440,
  }

  // Local state
  const [enabled, setEnabled] = useState(true)
  const [delayMinutes, setDelayMinutes] = useState('1440')

  // Fetch flow config - disabled in preview mode
  const { data: apiFlowConfig, isLoading, error } = useQuery({
    queryKey: ['flow'],
    queryFn: api.getFlowConfig,
    enabled: !isPreviewMode,
  })

  const flowConfig = isPreviewMode ? mockFlowConfig : apiFlowConfig

  // Update flow mutation
  const updateFlowMutation = useMutation({
    mutationFn: api.updateFlowConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow'] })
    },
  })

  // Initialize from config
  useEffect(() => {
    if (flowConfig) {
      setEnabled(flowConfig.enabled)
      setDelayMinutes(String(flowConfig.delay_minutes))
    }
  }, [flowConfig])

  // Handle toggle
  const handleToggle = () => {
    const newEnabled = !enabled
    setEnabled(newEnabled)
    updateFlowMutation.mutate({ enabled: newEnabled })
  }

  // Handle delay change
  const handleDelayChange = (value: string) => {
    setDelayMinutes(value)
    updateFlowMutation.mutate({ delay_minutes: parseInt(value) })
  }

  if (error && !isPreviewMode) {
    return (
      <Page title="Automation Flow">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load flow configuration. Please refresh the page.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title="Automation Flow"
      titleMetadata={
        <div style={{
          padding: '4px 12px',
          borderRadius: '20px',
          backgroundColor: enabled ? '#ff6b35' : '#e4e5e7',
          color: enabled ? '#fff' : '#6d7175',
          fontSize: '13px',
          fontWeight: 500,
        }}>
          {enabled ? 'Active' : 'Paused'}
        </div>
      }
      primaryAction={{
        content: enabled ? 'Pause Flow' : 'Activate Flow',
        onAction: handleToggle,
        loading: updateFlowMutation.isPending,
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Abandoned Cart Recovery Flow
              </Text>
              <Text as="p" tone="subdued">
                This automation sends personalized SMS messages to customers who abandon their checkout.
              </Text>
              <Divider />

              {isLoading ? (
                <SkeletonBodyText lines={10} />
              ) : (
                <div style={styles.flowContainer}>
                  {/* Trigger Node */}
                  <FlowNode
                    iconSrc="/icons/cart-dash-fill.png"
                    title="TRIGGER"
                    subtitle="Abandoned Checkout"
                  >
                    <Text as="p" variant="bodySm">
                      When a customer leaves items in cart without completing purchase
                    </Text>
                  </FlowNode>

                  {/* Wait Node */}
                  <FlowNode
                    iconSrc="/icons/alarm-fill.png"
                    title="WAIT"
                    subtitle={formatDelay(parseInt(delayMinutes))}
                  >
                    <Box paddingBlockStart="200">
                      <Select
                        label=""
                        options={delayOptions}
                        value={delayMinutes}
                        onChange={handleDelayChange}
                      />
                    </Box>
                  </FlowNode>

                  {/* Action Node */}
                  <FlowNode
                    iconSrc="/icons/chat-left-text-fill.png"
                    title="ACTION"
                    subtitle="Send SMS Message"
                    showConnector={false}
                  >
                    <InlineStack gap="200" align="start">
                      <div style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#fff5f2',
                        border: '1px solid #ff6b35',
                        color: '#ff6b35',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        Powered by Otty AI
                      </div>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Personalized message with smart discount offer
                    </Text>
                  </FlowNode>
                </div>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Info Card */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                How it works
              </Text>
              <BlockStack gap="300">
                <InlineStack gap="200">
                  <Text as="span">1.</Text>
                  <Text as="p" variant="bodySm">
                    Customer adds items to cart but doesn't checkout
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span">2.</Text>
                  <Text as="p" variant="bodySm">
                    We wait the specified time to give them a chance to return
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span">3.</Text>
                  <Text as="p" variant="bodySm">
                    Otty AI sends a personalized SMS with a smart offer
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span">4.</Text>
                  <Text as="p" variant="bodySm">
                    Customer completes purchase → you recover revenue!
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}

const styles: Record<string, React.CSSProperties> = {
  flowContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    borderRadius: '12px',
    backgroundImage: 'radial-gradient(#ff6b35 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    backgroundColor: '#fafafa',
  },
  nodeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  node: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px 20px',
    backgroundColor: '#fff',
    border: '2px solid #ff6b35',
    borderRadius: '12px',
    minWidth: '320px',
    maxWidth: '400px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  nodeIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#fff5f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  nodeContent: {
    flex: 1,
  },
  nodeChildren: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  connector: {
    width: '2px',
    height: '40px',
    backgroundColor: '#ff6b35',
    position: 'relative',
  },
}
